/**
 * Affiliate Create User Modal
 * 
 * Allows affiliates to create user accounts for their network members.
 * Automatically assigns the affiliate as the sponsor for commission tracking.
 */

import React, { useState, useEffect } from 'react'
import { validateEmail } from '../../lib/supabase'
import { hashPassword, validatePasswordStrength } from '../../lib/passwordSecurity'
import { getSessionHeaders } from '../../lib/serverSession'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''
import { logAdminAction } from '../../lib/adminAuth'
import { useCompleteCountryList } from '../../lib/hooks/useCompleteCountryList'

interface CreateUserModalProps {
  isOpen: boolean
  onClose: () => void
  onUserCreated: () => void
  affiliateUser: any // The affiliate creating the user
}

interface FormData {
  email: string
  username: string
  password: string
  confirmPassword: string
  fullName: string
  phone: string
  countryOfResidence: string
  sendWelcomeEmail: boolean
}

interface FormErrors {
  [key: string]: string
}

interface ValidationState {
  email: 'idle' | 'checking' | 'valid' | 'invalid'
  username: 'idle' | 'checking' | 'valid' | 'invalid'
  phone: 'idle' | 'checking' | 'valid' | 'invalid'
}

export const CreateUserModal: React.FC<CreateUserModalProps> = ({
  isOpen,
  onClose,
  onUserCreated,
  affiliateUser
}) => {
  // Get complete country list with South African countries prioritized
  const { countryOptions } = useCompleteCountryList({
    prioritizeCountries: ['ZAF', 'NAM', 'SWZ', 'BWA', 'ZWE', 'MOZ', 'LSO'] // Southern Africa priority
  })

  const [formData, setFormData] = useState<FormData>({
    email: '',
    username: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    phone: '',
    countryOfResidence: '', // FIXED: No default - affiliate must select country
    sendWelcomeEmail: true
  })

  const [errors, setErrors] = useState<FormErrors>({})
  const [loading, setLoading] = useState(false)
  const [generalError, setGeneralError] = useState('')
  const [showPassword, setShowPassword] = useState(true)
  const [showConfirmPassword, setShowConfirmPassword] = useState(true)
  const [validationState, setValidationState] = useState<ValidationState>({
    email: 'idle',
    username: 'idle',
    phone: 'idle'
  })

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        email: '',
        username: '',
        password: '',
        confirmPassword: '',
        fullName: '',
        phone: '',
        countryOfResidence: '', // FIXED: No default - affiliate must select country
        sendWelcomeEmail: true
      })
      setErrors({})
      setGeneralError('')
      setValidationState({
        email: 'idle',
        username: 'idle',
        phone: 'idle'
      })
    }
  }, [isOpen])

  // AJAX validation for duplicate checking
  const checkEmailAvailability = async (email: string): Promise<boolean> => {
    if (!email || !validateEmail(email)) return true

    try {
      setValidationState(prev => ({ ...prev, email: 'checking' }))

      const response = await fetch(`${API_BASE_URL}/api/auth/db-proxy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          action: 'check_email_exists',
          email: email.toLowerCase().trim()
        })
      })

      const result = await response.json()
      if (!response.ok) throw new Error(result.error)

      if (result.data?.exists) {
        setErrors(prev => ({ ...prev, email: 'This email is already registered' }))
        setValidationState(prev => ({ ...prev, email: 'invalid' }))
        return false
      }

      setValidationState(prev => ({ ...prev, email: 'valid' }))
      return true
    } catch (error) {
      console.error('Error checking email availability:', error)
      setValidationState(prev => ({ ...prev, email: 'idle' }))
      return true
    }
  }

  const checkUsernameAvailability = async (username: string): Promise<boolean> => {
    if (!username || username.length < 3) return true

    try {
      setValidationState(prev => ({ ...prev, username: 'checking' }))

      const response = await fetch(`${API_BASE_URL}/api/auth/db-proxy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          action: 'check_username_exists',
          username: username.toLowerCase().trim()
        })
      })

      const result = await response.json()
      if (!response.ok) throw new Error(result.error)

      if (result.data?.exists) {
        setErrors(prev => ({ ...prev, username: 'This username is already taken' }))
        setValidationState(prev => ({ ...prev, username: 'invalid' }))
        return false
      }

      setValidationState(prev => ({ ...prev, username: 'valid' }))
      return true
    } catch (error) {
      console.error('Error checking username availability:', error)
      setValidationState(prev => ({ ...prev, username: 'idle' }))
      return true
    }
  }

  const checkPhoneAvailability = async (phone: string): Promise<boolean> => {
    if (!phone || phone.length < 5) return true

    try {
      setValidationState(prev => ({ ...prev, phone: 'checking' }))

      const response = await fetch(`${API_BASE_URL}/api/auth/db-proxy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          action: 'check_phone_exists',
          phone: phone.trim()
        })
      })

      const result = await response.json()
      if (!response.ok) throw new Error(result.error)

      if (result.data?.exists) {
        setErrors(prev => ({ ...prev, phone: 'This phone number is already registered' }))
        setValidationState(prev => ({ ...prev, phone: 'invalid' }))
        return false
      }

      setValidationState(prev => ({ ...prev, phone: 'valid' }))
      return true
    } catch (error) {
      console.error('Error checking phone availability:', error)
      setValidationState(prev => ({ ...prev, phone: 'idle' }))
      return true
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target
    const checked = (e.target as HTMLInputElement).checked

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[name]
        return newErrors
      })
    }

    // Reset validation state when user types
    if (name === 'email' || name === 'username' || name === 'phone') {
      setValidationState(prev => ({ ...prev, [name]: 'idle' }))
    }
  }

  const handleBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target

    // Trigger AJAX validation on blur
    if (name === 'email' && value) {
      await checkEmailAvailability(value)
    } else if (name === 'username' && value) {
      await checkUsernameAvailability(value)
    } else if (name === 'phone' && value) {
      await checkPhoneAvailability(value)
    }
  }

  const validateForm = (): boolean => {
    console.log('🔍 [CreateUserModal] validateForm called with data:', {
      email: formData.email,
      username: formData.username,
      hasPassword: !!formData.password,
      hasConfirmPassword: !!formData.confirmPassword,
      fullName: formData.fullName,
      phone: formData.phone,
      country: formData.countryOfResidence
    })

    const newErrors: FormErrors = {}

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required'
      console.log('❌ [CreateUserModal] Email is required')
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Invalid email format'
      console.log('❌ [CreateUserModal] Invalid email format')
    }

    // Username validation
    if (!formData.username.trim()) {
      newErrors.username = 'Username is required'
      console.log('❌ [CreateUserModal] Username is required')
    } else if (formData.username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters'
      console.log('❌ [CreateUserModal] Username too short')
    } else if (!/^[a-zA-Z0-9_]+$/.test(formData.username)) {
      newErrors.username = 'Username can only contain letters, numbers, and underscores'
      console.log('❌ [CreateUserModal] Invalid username format')
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required'
      console.log('❌ [CreateUserModal] Password is required')
    } else {
      const passwordValidation = validatePasswordStrength(formData.password, formData.username)
      console.log('🔍 [CreateUserModal] Password validation result:', passwordValidation)
      if (!passwordValidation.valid) {
        newErrors.password = passwordValidation.errors.join(', ')
        console.log('❌ [CreateUserModal] Password validation failed:', passwordValidation.errors)
      }
    }

    // Confirm password
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match'
      console.log('❌ [CreateUserModal] Passwords do not match')
    }

    // Full name
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required'
      console.log('❌ [CreateUserModal] Full name is required')
    }

    // Phone
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required'
      console.log('❌ [CreateUserModal] Phone number is required')
    }

    // Country
    if (!formData.countryOfResidence) {
      newErrors.countryOfResidence = 'Country is required'
      console.log('❌ [CreateUserModal] Country is required')
    }

    setErrors(newErrors)
    const isValid = Object.keys(newErrors).length === 0
    console.log('🔍 [CreateUserModal] Validation result:', { isValid, errorCount: Object.keys(newErrors).length, errors: newErrors })
    return isValid
  }

  const checkForDuplicates = async (): Promise<boolean> => {
    try {
      // Check all three fields in parallel
      const [emailAvailable, usernameAvailable, phoneAvailable] = await Promise.all([
        checkEmailAvailability(formData.email),
        checkUsernameAvailability(formData.username),
        checkPhoneAvailability(formData.phone)
      ])

      if (!emailAvailable || !usernameAvailable || !phoneAvailable) {
        setGeneralError('Please fix the validation errors above')
        return false
      }

      return true
    } catch (error) {
      console.error('Error checking duplicates:', error)
      return true // Continue if check fails
    }
  }

  const createUser = async (): Promise<number | null> => {
    try {
      // Hash the password
      const hashedPassword = await hashPassword(formData.password)

      console.log('🔄 [CreateUserModal] Creating user account...')
      console.log('🔄 [CreateUserModal] Username:', formData.username)
      console.log('🔄 [CreateUserModal] Sponsor (affiliate):', affiliateUser.username)

      // Create user via API
      const response = await fetch(`${API_BASE_URL}/api/auth/db-proxy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          action: 'create_affiliate_user',
          userData: {
            email: formData.email.toLowerCase().trim(),
            password_hash: hashedPassword,
            full_name: formData.fullName.trim(),
            username: formData.username.toLowerCase().trim(),
            phone: formData.phone.trim(),
            phone_number: formData.phone.trim(),
            country_of_residence: formData.countryOfResidence,
            is_admin: false,
            is_active: true,
            email_verified: false
          },
          affiliateId: affiliateUser.id,
          affiliateUsername: affiliateUser.username
        })
      })

      const result = await response.json()
      if (!response.ok) {
        console.error('❌ Error creating user:', result.error)
        setGeneralError(`Failed to create user: ${result.error}`)
        return null
      }

      const { user: userData, referralCreated, commissionBalanceCreated } = result.data

      console.log('✅ [CreateUserModal] User created:', userData)

      if (!referralCreated) {
        console.warn('⚠️ Referral relationship was not created')
      } else {
        console.log('✅ [CreateUserModal] Referral relationship created')
      }

      if (!commissionBalanceCreated) {
        console.warn('⚠️ Commission balance was not created')
      } else {
        console.log('✅ [CreateUserModal] Commission balance initialized')
      }

      // Log affiliate action
      try {
        await logAdminAction(
          affiliateUser.email,
          'AFFILIATE_USER_CREATION',
          'user',
          userData.id.toString(),
          {
            affiliate_username: affiliateUser.username,
            affiliate_id: affiliateUser.id,
            new_user_username: userData.username,
            new_user_email: formData.email,
            new_user_full_name: formData.fullName,
            referral_assigned: referralCreated,
            welcome_email_sent: formData.sendWelcomeEmail
          }
        )
        console.log('✅ [CreateUserModal] Affiliate action logged')
      } catch (auditError) {
        console.warn('⚠️ Failed to log affiliate action:', auditError)
      }

      return userData.id
    } catch (error) {
      console.error('❌ Error creating user:', error)
      setGeneralError('Failed to create user. Please try again.')
      return null
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    console.log('🚀 [CreateUserModal] Form submitted')
    setGeneralError('')

    console.log('🔍 [CreateUserModal] Validating form...')
    if (!validateForm()) {
      console.error('❌ [CreateUserModal] Form validation failed')
      return
    }
    console.log('✅ [CreateUserModal] Form validation passed')

    setLoading(true)

    try {
      // Check for duplicates
      console.log('🔍 [CreateUserModal] Checking for duplicates...')
      const noDuplicates = await checkForDuplicates()
      if (!noDuplicates) {
        console.error('❌ [CreateUserModal] Duplicate check failed')
        setLoading(false)
        return
      }
      console.log('✅ [CreateUserModal] No duplicates found')

      // Create the user
      console.log('👤 [CreateUserModal] Creating user...')
      const userId = await createUser()
      if (!userId) {
        console.error('❌ [CreateUserModal] User creation failed')
        setLoading(false)
        return
      }
      console.log('✅ [CreateUserModal] User created successfully, ID:', userId)

      // Send welcome email with credentials if requested
      if (formData.sendWelcomeEmail) {
        try {
          console.log('📧 [CreateUserModal] Sending welcome email...')

          const loginUrl = `${window.location.origin}/login`

          const emailResponse = await fetch('/api/send-email', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              emailType: 'welcome',
              data: {
                email: formData.email,
                username: formData.username,
                fullName: formData.fullName,
                password: formData.password,
                loginUrl: loginUrl
              }
            })
          })

          const emailResult = await emailResponse.json()

          if (emailResult.success) {
            console.log('✅ [CreateUserModal] Welcome email sent')
          } else {
            console.warn('⚠️ [CreateUserModal] Welcome email failed:', emailResult.error)
          }
        } catch (emailError) {
          console.error('❌ [CreateUserModal] Error sending email:', emailError)
        }
      }

      // Success!
      setGeneralError('')
      onUserCreated()
      onClose()
    } catch (error) {
      console.error('❌ Error in form submission:', error)
      setGeneralError('Failed to create user. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 pt-8 overflow-y-auto">
      <div className="bg-gray-900 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto mb-8">
        {/* Header */}
        <div className="sticky top-0 bg-gray-900 border-b border-gray-700 px-6 py-4 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-yellow-500">Create User Account</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white text-2xl"
            disabled={loading}
          >
            ×
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {generalError && (
            <div className="bg-red-900 border border-red-700 text-red-200 px-4 py-3 rounded">
              {generalError}
            </div>
          )}

          <div className="bg-blue-900 border border-blue-700 text-blue-200 px-4 py-3 rounded text-sm">
            <p className="font-semibold mb-1">ℹ️ Automatic Referral Assignment</p>
            <p>This user will be automatically assigned as your direct referral. You will earn 10% USDT + 5% automatic share purchase on their purchases.</p>
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Email Address <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                onBlur={handleBlur}
                className={`w-full px-3 py-2 bg-gray-800 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 ${
                  errors.email ? 'border-red-500' : validationState.email === 'valid' ? 'border-green-500' : 'border-gray-600'
                }`}
                placeholder="user@example.com"
                disabled={loading}
              />
              {validationState.email === 'checking' && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <div className="animate-spin h-5 w-5 border-2 border-yellow-500 border-t-transparent rounded-full"></div>
                </div>
              )}
              {validationState.email === 'valid' && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-green-500">
                  ✓
                </div>
              )}
            </div>
            {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
            <p className="text-white text-sm mt-1">User will need to verify email on first login</p>
          </div>

          {/* Username */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Username <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                onBlur={handleBlur}
                className={`w-full px-3 py-2 bg-gray-800 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 ${
                  errors.username ? 'border-red-500' : validationState.username === 'valid' ? 'border-green-500' : 'border-gray-600'
                }`}
                placeholder="johndoe123"
                disabled={loading}
              />
              {validationState.username === 'checking' && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <div className="animate-spin h-5 w-5 border-2 border-yellow-500 border-t-transparent rounded-full"></div>
                </div>
              )}
              {validationState.username === 'valid' && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-green-500">
                  ✓
                </div>
              )}
            </div>
            {errors.username && <p className="text-red-500 text-sm mt-1">{errors.username}</p>}
            <p className="text-white text-sm mt-1">Letters, numbers, and underscores only. Minimum 3 characters.</p>
          </div>

          {/* Full Name */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Full Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="fullName"
              value={formData.fullName}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 bg-gray-800 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 ${
                errors.fullName ? 'border-red-500' : 'border-gray-600'
              }`}
              placeholder="John Doe"
              disabled={loading}
            />
            {errors.fullName && <p className="text-red-500 text-sm mt-1">{errors.fullName}</p>}
          </div>

          {/* Phone Number */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Phone Number <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                onBlur={handleBlur}
                className={`w-full px-3 py-2 bg-gray-800 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 ${
                  errors.phone ? 'border-red-500' : validationState.phone === 'valid' ? 'border-green-500' : 'border-gray-600'
                }`}
                placeholder="+27 123 456 789"
                disabled={loading}
              />
              {validationState.phone === 'checking' && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <div className="animate-spin h-5 w-5 border-2 border-yellow-500 border-t-transparent rounded-full"></div>
                </div>
              )}
              {validationState.phone === 'valid' && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-green-500">
                  ✓
                </div>
              )}
            </div>
            {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
          </div>

          {/* Country of Residence */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Country of Residence <span className="text-red-500">*</span>
            </label>
            <select
              name="countryOfResidence"
              value={formData.countryOfResidence}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 bg-gray-800 border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 ${
                errors.countryOfResidence ? 'border-red-500' : 'border-gray-600'
              }`}
              disabled={loading}
            >
              <option value="">— Select country —</option>
              {countryOptions.map(country => (
                <option key={country.value} value={country.value}>
                  {country.flag} {country.label}
                </option>
              ))}
            </select>
            {errors.countryOfResidence && <p className="text-red-500 text-sm mt-1">{errors.countryOfResidence}</p>}
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Temporary Password <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 bg-gray-800 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 ${
                  errors.password ? 'border-red-500' : 'border-gray-600'
                }`}
                placeholder="Enter temporary password"
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
            {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
            <p className="text-white text-sm mt-1">
              Must be 8+ characters with uppercase, lowercase, number, and special character
            </p>
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Confirm Password <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 bg-gray-800 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 ${
                  errors.confirmPassword ? 'border-red-500' : 'border-gray-600'
                }`}
                placeholder="Confirm password"
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
              >
                {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
            {errors.confirmPassword && <p className="text-red-500 text-sm mt-1">{errors.confirmPassword}</p>}
          </div>

          {/* Send Welcome Email */}
          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                name="sendWelcomeEmail"
                checked={formData.sendWelcomeEmail}
                onChange={handleInputChange}
                className="mr-2 rounded border-gray-600 bg-gray-800 text-yellow-500 focus:ring-yellow-500"
                disabled={loading}
              />
              <span className="text-sm text-gray-300">Send welcome email with login credentials</span>
            </label>
            <p className="text-white text-sm mt-1 ml-6">
              Recommended: User will receive email with username and password
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-yellow-500 text-black font-semibold rounded-lg hover:bg-yellow-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? 'Creating User...' : 'Create User Account'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

